/**
 * AdjustDrCrView
 *
 * Renders adjustment rows classified into Dr (top) and Cr (bottom) sections.
 * Only rows with non-zero adjustments are shown (plus any manually-added rows).
 * Each row displays: item name, schedule name, perClient (read-only), adjustment (editable).
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {MoneyField} from '@/pages/components/common/MoneyField';
import {InputField} from '@/pages/components/common/InputField';
import {
    type AdjustedRowInfo,
    classifyLinesForDrCrView,
    computeFromDtdClEffectiveEntry,
    type DrCrRenderItem,
    type DtdClBsClPairingCtx,
    type LineDrCrContext,
    formatAmount,
    resolveLineSignedAdjustment,
    scanAllRows
} from '@/pages/components/audit/adjustment/adjust/utils/classifyRows';
import {
    computeLineAdjustmentFromUserInput,
    getOriginalEntryFromFsCategory,
    getOriginalEntryFromParent,
    isDtdClFsCode,
    usesDtdAdjustmentPathScoped,
    resolveSignedAdjustmentFromBackendLine,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {
    computeDtdPathFromEffectiveEntry,
} from '@/pages/components/audit/adjustment/adjust/utils/specialAdjustFlow';
import {
    computeTaxSalePositiveOnlyAdjustment,
    getTaxSaleRenderRowKey,
} from '@/wp/tax/taxSelfAdjustment';
import {useAdjustDraftStore, useCompanyAnnualStore, useFsAdjustStore} from '@/store/audit';
import type {AuditAdjustLine} from '@/services/auditService/auditAdjustOrchestrate';

function unwrapLineDetail(details: unknown): any {
    if (details == null) return null;
    if (Array.isArray(details)) {
        if (details.length === 0) return null;
        let d: any = details[0];
        if (d && typeof d === 'object' && !Array.isArray(d) && d.payload && typeof d.payload === 'object') {
            d = d.payload;
        }
        return d;
    }
    if (typeof details === 'object' && (details as any).payload && typeof (details as any).payload === 'object') {
        return (details as any).payload;
    }
    return details;
}

function resolveLineCode(line: AuditAdjustLine): string {
    return String(line.code || '').trim().toUpperCase();
}

function parseAllLinesForRender(
    lines: AuditAdjustLine[],
    renderOpts?: {
        pendingAppendCodes?: Set<string>;
        pendingAppendAdjustments?: Record<string, number>;
        draftAppendAdjustments?: Record<string, number | undefined>;
        toUserEditedCodes?: Set<string>;
        fromPaperUserEdited?: boolean;
        toOriginalEntries?: Record<string, string | null>;
        staticPositionCodes?: Set<string>;
        specialDtdPathCodes?: Set<string>;
    },
): LineDrCrContext[] | null {
    if (!lines || lines.length < 2) return null;

    const fsAdjustState = useFsAdjustStore.getState();
    const contexts: LineDrCrContext[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const code = resolveLineCode(line);
        if (!code) continue;

        const codeUpper = String(code).trim().toUpperCase();
        const lineFromStore = fsAdjustState.getLineByCode(code);
        const codeName = lineFromStore?.name
            || lineFromStore?.scheduleName
            || code;
        const isToLine = i > 0;
        // special 路径（含 TAXP Dr-side）：优先 getOriginalEntryFromParent，勿被
        // toOriginalEntries 的 FS 负债 Cr 盖掉（否则 Tabs 编辑后分列跳到对侧）
        const originalEntry = usesDtdAdjustmentPathScoped(codeUpper, renderOpts?.specialDtdPathCodes)
            ? getOriginalEntryFromParent(codeUpper)
            : (
                (isToLine && renderOpts?.toOriginalEntries?.[codeUpper])
                || getOriginalEntryFromFsCategory(code)
            );
        const effectiveEntry = (line.entry === 'Dr' || line.entry === 'Cr')
            ? line.entry
            : undefined;
        const parDetail = unwrapLineDetail(line.details);
        const lineAmount = Number(line.adjustmentAmount) || 0;
        const pendingAdj = Number(renderOpts?.pendingAppendAdjustments?.[codeUpper]) || 0;
        const draftAppendAdj = Number(renderOpts?.draftAppendAdjustments?.[codeUpper]) || 0;
        const isBackendStaticLine = renderOpts?.staticPositionCodes?.has(codeUpper) ?? false;
        const preferLineAmount = isToLine && (
            renderOpts?.pendingAppendCodes?.has(codeUpper)
            || renderOpts?.toUserEditedCodes?.has(codeUpper)
        );
        const fromUserEdited = i === 0 && !!renderOpts?.fromPaperUserEdited;
        const toUserEdited = isToLine && !!renderOpts?.toUserEditedCodes?.has(codeUpper);
        // DTD 路径 fromPaper 后端回显：锁定 line.entry（不影响 toPaper / 非 DTD 节点）
        const isDtdFromBackendEcho = i === 0
            && usesDtdAdjustmentPathScoped(codeUpper, renderOpts?.specialDtdPathCodes)
            && !!effectiveEntry
            && !fromUserEdited;
        const isPendingAppendInit = isToLine
            && !!renderOpts?.pendingAppendCodes?.has(codeUpper)
            && !toUserEdited;
        const signedAppendAdj = Math.abs(pendingAdj) >= 0.001
            ? pendingAdj
            : (Math.abs(draftAppendAdj) >= 0.001 ? draftAppendAdj : 0);
        const absLineAmount = Math.abs(lineAmount) >= 0.001
            ? Math.abs(lineAmount)
            : Math.abs(signedAppendAdj);
        const useNonDtdAuthoritativeLineEntry = !!effectiveEntry
            && absLineAmount >= 0.001
            && !fromUserEdited
            && !toUserEdited
            && (isPendingAppendInit || (isBackendStaticLine && !preferLineAmount));
        const useAuthoritativeLineEntry = isDtdFromBackendEcho || useNonDtdAuthoritativeLineEntry;
        let lineAdjustment: number;
        if (useAuthoritativeLineEntry) {
            lineAdjustment = resolveSignedAdjustmentFromBackendLine(
                effectiveEntry!,
                absLineAmount,
                code,
                renderOpts?.specialDtdPathCodes,
            );
        } else if (preferLineAmount) {
            lineAdjustment = Math.abs(lineAmount) >= 0.001
                ? lineAmount
                : signedAppendAdj;
        } else {
            lineAdjustment = resolveLineSignedAdjustment(parDetail, lineAmount, code, {
                effectiveEntry,
                useBackendLineAmount: isBackendStaticLine && !!effectiveEntry,
                useAuthoritativeLineEntry: false,
                specialDtdPathCodes: renderOpts?.specialDtdPathCodes,
            });
        }

        contexts.push({
            code,
            codeName,
            originalEntry,
            effectiveEntry,
            parDetail,
            lineAdjustment,
            source: i === 0 ? 'from' : 'to',
            toIndex: i > 0 ? i - 1 : undefined,
        });
    }

    return contexts.length >= 2 ? contexts : null;
}

export interface AdjustDrCrViewProps {
    /** adjustment lines：lines[0] 为 from，lines[1..] 为 to（结构与提交 JSON 一致） */
    lines: AuditAdjustLine[];

    companyId?: number;
    annualId?: number;
    onToDetailLoaded?: (code: string, detail: any) => void;

    onAdjustmentChange?: (code: string, arrayName: string, rowIndex: number, newAdjustment: number, subField?: string) => void;
    onItemNameChange?: (code: string, arrayName: string, rowIndex: number, newName: string) => void;
    onBankFieldChange?: (code: string, arrayName: string, rowIndex: number, field: 'bankName' | 'accountNumber', value: string) => void;
    /** Field-level validation errors for BS-CA-CASH bankRow { [code]: { [rowIndex]: { bankName?, accountNumber? } } } */
    bankFieldErrors?: Record<string, Record<number, { bankName?: string; accountNumber?: string }>>;
    /** Triggered when a toPaper endpoint has no rendered rows left after deleting a row */
    onEndpointDepleted?: (code: string) => void;
    onAddToPaper?: () => void;
    onBalanceChange?: (isBalanced: boolean, diffAmount?: number, diffSide?: 'Dr' | 'Cr') => void;
    /** @deprecated entry 已由 adjustDraftStore.updateLineAdjustment 同步，保留仅为兼容 */
    onEntryChange?: (code: string, newEntry: 'Dr' | 'Cr') => void;

    readOnly?: boolean;
    /** @deprecated 动态分列不再使用 staticPositionCodes 锁定 */
    staticPositionCodes?: Set<string>;

    /** Remark field value */
    remark?: string;
    /** Callback when remark changes */
    onRemarkChange?: (value: string) => void;

    /** 从父级（AdjustTabsDialog）传递的初始 extraRowKeys，确保父级创建的行在子组件中可见 */
    initialExtraRowKeys?: Set<string>;

    /** Tax Sale 回显编辑：锁定初始 Dr/Cr 列，仅允许在该列输入正数 */
    taxSalePositiveOnlyEdit?: boolean;
}

// ── Internal: single Dr/Cr section ──────────────────────────────────────

function DrCrSection({
                         title,
                         color,
                         items,
                         onAdjustmentChange,
                         onItemNameChange,
                         onBankFieldChange,
                         bankFieldErrors,
                         onAddRow,
                         onDeleteRow,
                         readOnly,
                         currencySymbol,
                         availableRowCounts,
                         frozenFromKeys,
                         taxSalePositiveOnlyEdit,
                         taxSaleLockedPositions,
                         specialDtdPathCodes,
                     }: {
    title: string;
    color: string;
    items: DrCrRenderItem[];
    onAdjustmentChange: (code: string, arrayName: string, rowIndex: number, value: number, subField?: string) => void;
    onItemNameChange?: (code: string, arrayName: string, rowIndex: number, newName: string) => void;
    onBankFieldChange?: (code: string, arrayName: string, rowIndex: number, field: 'bankName' | 'accountNumber', value: string) => void;
    bankFieldErrors?: Record<string, Record<number, { bankName?: string; accountNumber?: string }>>;
    onAddRow?: (code: string) => void;
    onDeleteRow?: (code: string, arrayName: string, rowIndex: number) => void;
    readOnly?: boolean;
    currencySymbol?: string;
    availableRowCounts?: Record<string, number>;
    frozenFromKeys?: Set<string>;
    taxSalePositiveOnlyEdit?: boolean;
    taxSaleLockedPositions?: ReadonlyMap<string, 'Dr' | 'Cr'>;
    specialDtdPathCodes?: Set<string>;
}) {
    if (items.length === 0) return null;

    const resolveLockedRenderPosition = (item: DrCrRenderItem): 'Dr' | 'Cr' => {
        if (!taxSalePositiveOnlyEdit || !taxSaleLockedPositions) return item.position;
        return taxSaleLockedPositions.get(getTaxSaleRenderRowKey(item)) ?? item.position;
    };

    /** 渲染合成行：无 detail 时按 line 级 adjustment 展示 */
    const renderSyntheticRow = (item: DrCrRenderItem) => {
        const isEditable = !readOnly;
        const renderInDrColumn = resolveLockedRenderPosition(item) === 'Dr';

        const renderAdjustmentCell = () => {
            if (isEditable) {
                return renderAdjustmentField(item, true);
            }
            return <Typography variant="body2">{formatAmount(item.adjustment)}</Typography>;
        };

        return (
            <TableRow
                key={`${item.code}-synthetic`}
                hover sx={{'&:last-child td': {borderBottom: 'none'}}}
            >
                <TableCell sx={{py: 0.5}}>
                    <Typography variant="body2" noWrap>
                        {item.codeName} <Typography component="span" variant="caption"
                                                    color="text.secondary">({item.code})</Typography>
                    </Typography>
                </TableCell>
                {renderInDrColumn ? (
                    <>
                        <TableCell align="center" sx={{py: 0.5, width: 140}}>
                            {renderAdjustmentCell()}
                        </TableCell>
                        <TableCell sx={{py: 0.5, width: 140}}/>
                    </>
                ) : (
                    <>
                        <TableCell sx={{py: 0.5, width: 140}}/>
                        <TableCell align="center" sx={{py: 0.5, width: 140}}>
                            {renderAdjustmentCell()}
                        </TableCell>
                    </>
                )}
                {!readOnly && (
                    <TableCell sx={{py: 0.5, width: 80}}/>
                )}
            </TableRow>
        );
    };

    /** 统一：根据 FS originalEntry + 当前列 + 用户输入计算存储 adjustment */
    const applyAdjustmentInput = (item: DrCrRenderItem, val: number) => {
        const renderPosition = resolveLockedRenderPosition(item);
        const inputVal = taxSalePositiveOnlyEdit ? Math.abs(Number(val) || 0) : val;
        const isScopedDtd = usesDtdAdjustmentPathScoped(item.code, specialDtdPathCodes);
        const originalEntry = isScopedDtd
            ? getOriginalEntryFromParent(item.code)
            : (item.originalEntry || getOriginalEntryFromFsCategory(item.code));
        const storedAdjustment = taxSalePositiveOnlyEdit
            ? computeTaxSalePositiveOnlyAdjustment(
                {
                    code: item.code,
                    arrayName: item.arrayName,
                    rowIndex: item.rowIndex,
                    row: item.row,
                    isSynthetic: item.isSynthetic,
                    originalEntry: item.originalEntry,
                },
                renderPosition,
                inputVal,
            )
            : computeLineAdjustmentFromUserInput(
                item.code,
                renderPosition,
                inputVal,
                item.adjustment,
                originalEntry,
                specialDtdPathCodes,
            );
        onAdjustmentChange(
            item.code,
            item.arrayName,
            item.rowIndex,
            storedAdjustment,
            item.subField,
        );
    };

    const renderAdjustmentField = (item: DrCrRenderItem, editable: boolean) => (
        <MoneyField
            value={Math.abs(item.adjustment)}
            currencySymbol={currencySymbol}
            editable={editable}
            onChangeNumber={(val) => applyAdjustmentInput(item, val)}
            compact
            tiny
            align="center"
        />
    );

    /** Render item name fields — special case for BS-CA-CASH bankRow (Bank Name + Account No.) */
    const renderItemFields = (item: DrCrRenderItem) => {
        if (item.code === 'BS-CA-CASH' && item.arrayName === 'bankRow') {
            if (!readOnly && onBankFieldChange) {
                return (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <InputField
                            variant="outlined"
                            size="small"
                            align="left"
                            label="Name of Bank"
                            value={item.row.bankName || ''}
                            onChange={(e) => onBankFieldChange(item.code, item.arrayName, item.rowIndex, 'bankName', (e.target as HTMLInputElement).value)}
                            error={!!bankFieldErrors?.[item.code]?.[item.rowIndex]?.bankName}
                            helperText={bankFieldErrors?.[item.code]?.[item.rowIndex]?.bankName}
                            sx={{minWidth: 100, flex: 1}}
                        />
                        <InputField
                            variant="outlined"
                            size="small"
                            type="text"
                            align="left"
                            label="Bank Account No#"
                            value={item.row.accountNumber || ''}
                            onChange={(e) => onBankFieldChange(item.code, item.arrayName, item.rowIndex, 'accountNumber', (e.target as HTMLInputElement).value)}
                            error={!!bankFieldErrors?.[item.code]?.[item.rowIndex]?.accountNumber}
                            helperText={bankFieldErrors?.[item.code]?.[item.rowIndex]?.accountNumber}
                            sx={{minWidth: 100, flex: 1}}
                        />
                        <Typography variant="body2" noWrap>- ({item.codeName})</Typography>
                    </Box>
                );
            }
            return (
                <Typography variant="body2" noWrap>
                    {item.row.bankName || ''} / {item.row.accountNumber || ''} - ({item.codeName})
                </Typography>
            );
        }

        if (!readOnly && onItemNameChange) {
            return (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <InputField
                        value={item.row.item || ''}
                        onChange={(e) => onItemNameChange(item.code, item.arrayName, item.rowIndex, (e.target as HTMLInputElement).value)}
                        compact
                        size="small"
                        sx={{minWidth: 80, flex: 1}}
                    />
                    <Typography variant="body2" noWrap>- ({item.codeName})</Typography>
                </Box>
            );
        }
        return (
            <Typography variant="body2" noWrap>
                {item.row.item} - ({item.codeName})
            </Typography>
        );
    };

    return (
        <TableBody>
            {items.map((item, idx) => (
                item.isSynthetic ? renderSyntheticRow(item) : (
                    <TableRow
                        key={`${item.code}-${item.arrayName}-${item.rowIndex}`}
                        hover sx={{'&:last-child td': {borderBottom: 'none'}}}>
                        <TableCell sx={{py: 0.5}}>
                            {renderItemFields(item)}
                        </TableCell>
                        {resolveLockedRenderPosition(item) === 'Dr' ? (
                            <>
                                <TableCell align="center" sx={{py: 0.5, width: 140}}>
                                    {renderAdjustmentField(
                                        item,
                                        !readOnly && !frozenFromKeys?.has(`${item.code}:${item.arrayName}:${item.rowIndex}`),
                                    )}
                                </TableCell>
                                <TableCell sx={{py: 0.5, width: 140}}/>
                            </>
                        ) : (
                            <>
                                <TableCell sx={{py: 0.5, width: 140}}/>
                                <TableCell align="center" sx={{py: 0.5, width: 140}}>
                                    {renderAdjustmentField(
                                        item,
                                        !readOnly && !frozenFromKeys?.has(`${item.code}:${item.arrayName}:${item.rowIndex}`),
                                    )}
                                </TableCell>
                            </>
                        )}
                        {!readOnly && (
                            <TableCell align="center" sx={{py: 0.5, width: 80, whiteSpace: 'nowrap'}}>
                                {onAddRow && (availableRowCounts?.[item.code] ?? 0) > 0 && (
                                    <Tooltip title={`Add Row (${item.codeName})`}>
                                        <IconButton size="small" onClick={() => onAddRow(item.code)}>
                                            <AddIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {onDeleteRow && !frozenFromKeys?.has(`${item.code}:${item.arrayName}:${item.rowIndex}`) && (
                                    <Tooltip title="Delete">
                                        <IconButton size="small"
                                                    onClick={() => onDeleteRow(item.code, item.arrayName, item.rowIndex)}>
                                            <DeleteOutlineIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </TableCell>
                        )}
                    </TableRow>
                )
            ))}
            <TableRow>
                <TableCell colSpan={readOnly ? 3 : 4} sx={{py: 0.25, verticalAlign: 'middle'}}/>
            </TableRow>
        </TableBody>
    );
}

// ── Internal: Add-row picker dialog ─────────────────────────────────────

interface RowPickerProps {
    open: boolean;
    onClose: () => void;
    endpoints: Array<{ code: string; codeName: string; entry: string; parDetail: any }>;
    shownKeys: Set<string>;
    onSelect: (code: string, arrayName: string, rowIndex: number) => void;
}

function RowPickerDialog({open, onClose, endpoints, shownKeys, onSelect}: RowPickerProps) {
    const availableRows = useMemo(() => {
        const result: Array<{ code: string; codeName: string; entry: string; info: AdjustedRowInfo }> = [];
        for (const ep of endpoints) {
            const allRows = scanAllRows(ep.parDetail);
            for (const row of allRows) {
                const key = `${ep.code}:${row.arrayName}:${row.rowIndex}`;
                if (!shownKeys.has(key)) {
                    result.push({code: ep.code, codeName: ep.codeName, entry: ep.entry, info: row});
                }
            }
        }
        return result;
    }, [endpoints, shownKeys]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Select rows to add</DialogTitle>
            <DialogContent dividers>
                {availableRows.length === 0 ? (
                    <Typography color="text.secondary" sx={{py: 2, textAlign: 'center'}}>
                        No additional rows available.
                    </Typography>
                ) : (
                    <List dense disablePadding>
                        {availableRows.map((r) => (
                            <ListItemButton
                                key={`${r.code}-${r.info.arrayName}-${r.info.rowIndex}`}
                                onClick={() => {
                                    onSelect(r.code, r.info.arrayName, r.info.rowIndex);
                                    onClose();
                                }}
                            >
                                <ListItemText
                                    primary={r.info.rowKey}
                                    secondary={`${r.codeName} (${r.entry})`}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── Main component ──────────────────────────────────────────────────────

export default function AdjustDrCrView({
                                           lines,
                                           companyId, annualId,
                                           onToDetailLoaded, onAdjustmentChange,
                                           onItemNameChange, onBankFieldChange,
                                           bankFieldErrors,
                                           onEndpointDepleted, onAddToPaper,
                                           onBalanceChange, readOnly,
                                           remark, onRemarkChange,
                                           initialExtraRowKeys,
                                           taxSalePositiveOnlyEdit,
                                       }: AdjustDrCrViewProps) {
    const fromPaper = useAdjustDraftStore((state) => state.fromPaper);
    const toPaper = useAdjustDraftStore((state) => state.toPaper);
    const toPaperUserEditedCodes = useAdjustDraftStore((state) => state.toPaperUserEditedCodes);
    const pendingAppendAdjustments = useAdjustDraftStore((state) => state.pendingAppendAdjustments);
    const draftToPaperAdjustments = useAdjustDraftStore((state) => state.draftToPaperAdjustments);
    const fromPaperUserEdited = useAdjustDraftStore((state) => state.fromPaperUserEdited);
    const staticPositionCodes = useAdjustDraftStore((state) => state.staticPositionCodes);
    const specialDtdPathCodes = useAdjustDraftStore((state) => state.specialDtdPathCodes);

    const toUserEditedCodeSet = useMemo(() => new Set(
        Object.entries(toPaperUserEditedCodes)
            .filter(([, edited]) => edited)
            .map(([code]) => String(code || '').trim().toUpperCase())
            .filter(Boolean),
    ), [toPaperUserEditedCodes]);

    const pendingAppendCodeSet = useMemo(() => new Set(
        Object.keys(pendingAppendAdjustments)
            .map((code) => String(code || '').trim().toUpperCase())
            .filter(Boolean),
    ), [pendingAppendAdjustments]);

    const toOriginalEntries = useMemo(
        () => toPaper?.originalEntries || {},
        [toPaper?.originalEntries],
    );

    const lineContexts = useMemo(
        () => parseAllLinesForRender(lines, {
            pendingAppendCodes: pendingAppendCodeSet,
            pendingAppendAdjustments,
            draftAppendAdjustments: draftToPaperAdjustments,
            toUserEditedCodes: toUserEditedCodeSet,
            fromPaperUserEdited,
            toOriginalEntries,
            staticPositionCodes,
            specialDtdPathCodes,
        }),
        [lines, pendingAppendCodeSet, pendingAppendAdjustments, draftToPaperAdjustments, toUserEditedCodeSet, fromPaperUserEdited, toOriginalEntries, staticPositionCodes, specialDtdPathCodes],
    );

    const dtdPairingCtx = useMemo((): DtdClBsClPairingCtx | null => {
        const fromCode = String(fromPaper?.code || '').trim().toUpperCase();
        const fromUsesDtdPath = usesDtdAdjustmentPathScoped(fromCode, specialDtdPathCodes);
        if (!fromCode || !fromUsesDtdPath) return null;

        const fromCtx = lineContexts?.[0];
        const fromDtdClUserEdited = fromPaperUserEdited;
        const fromDtdEffectiveEntry = isDtdClFsCode(fromCode)
            ? computeFromDtdClEffectiveEntry(
                fromCtx?.parDetail,
                fromPaper?.adjustment,
                fromDtdClUserEdited,
                fromCode,
            )
            : computeDtdPathFromEffectiveEntry(
                fromCode,
                fromCtx?.parDetail,
                fromPaper?.adjustment,
                fromDtdClUserEdited,
            );
        const resolvedFromEntry = fromDtdEffectiveEntry ?? (
            fromPaper?.entry === 'Dr' || fromPaper?.entry === 'Cr'
                ? fromPaper.entry
                : null
        );

        const dtdClToUserEdited = new Set(
            Object.entries(toPaperUserEditedCodes)
                .filter(([, edited]) => edited)
                .map(([code]) => String(code || '').trim().toUpperCase())
                .filter(Boolean),
        );

        return {
            fromCode,
            fromAdjustment: fromPaper?.adjustment,
            fromDtdClUserEdited,
            fromDtdEffectiveEntry: resolvedFromEntry,
            dtdClToUserEdited,
        };
    }, [
        fromPaper?.code,
        fromPaper?.adjustment,
        fromPaper?.entry,
        lineContexts,
        toPaperUserEditedCodes,
        fromPaperUserEdited,
        specialDtdPathCodes,
    ]);

    const dtdOrigEntries = useMemo(() => {
        const entries: Record<string, string> = {};
        const fromCode = String(fromPaper?.code || '').trim().toUpperCase();
        if (fromCode && fromPaper?.entry) {
            entries[fromCode] = fromPaper.entry;
        }
        const originalEntries = toPaper?.originalEntries || {};
        for (const [code, entry] of Object.entries(originalEntries)) {
            const nk = String(code || '').trim().toUpperCase();
            if (nk && entry) {
                entries[nk] = entry;
            }
        }
        return entries;
    }, [fromPaper?.code, fromPaper?.entry, toPaper?.originalEntries]);

    const fromCode = lineContexts?.[0]?.code ?? '';
    const toCodes = useMemo(
        () => (lineContexts ?? []).slice(1).map((ctx) => ctx.code),
        [lineContexts],
    );

    // Track manually-added rows (keys like "code:arrayName:rowIndex")
    const [extraRowKeys, setExtraRowKeys] = useState<Set<string>>(new Set());
    const taxSaleLockedPositionsRef = useRef<Map<string, 'Dr' | 'Cr'>>(new Map());

    useEffect(() => {
        if (!taxSalePositiveOnlyEdit) {
            taxSaleLockedPositionsRef.current = new Map();
        }
    }, [taxSalePositiveOnlyEdit]);

    // ✅ 合并父级传递的初始 extraRowKeys 与本地状态
    useEffect(() => {
        if (initialExtraRowKeys && initialExtraRowKeys.size > 0) {
            setExtraRowKeys(prev => {
                const merged = new Set(prev);
                let changed = false;
                for (const key of initialExtraRowKeys) {
                    if (!merged.has(key)) {
                        merged.add(key);
                        changed = true;
                    }
                }
                return changed ? merged : prev;
            });
        }
    }, [initialExtraRowKeys]);

    // ✅ 所有 Dr/Cr 列均可编辑，不再冻结 fromPaper 行
    const frozenFromKeys = new Set<string>();

    // Fetch presentationCurrency for MoneyField prefix
    const [currencySymbol, setCurrencySymbol] = useState('');
    useEffect(() => {
        if (!annualId) return;
        let cancelled = false;
        (async () => {
            try {
                const cur = await useCompanyAnnualStore.getState().getPresentationCurrency(Number(annualId));
                if (!cancelled) setCurrencySymbol(cur || '');
            } catch {
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [annualId]);
    const [pickerForCode, setPickerForCode] = useState<string | null>(null);

    const handleAdjustmentChange = useCallback((
        code: string,
        arrayName: string,
        rowIndex: number,
        newAdjustment: number,
        subField?: string,
    ) => {
        useAdjustDraftStore.getState().updateLineAdjustment(
            code,
            arrayName,
            rowIndex,
            newAdjustment,
            subField,
        );
        onAdjustmentChange?.(code, arrayName, rowIndex, newAdjustment, subField);
    }, [onAdjustmentChange]);

    const classifyOptions = useMemo(() => ({
        dtdCtx: dtdPairingCtx,
        fromPaperUserEdited,
        toUserEditedCodes: toUserEditedCodeSet,
        pendingAppendCodes: pendingAppendCodeSet,
        pendingAppendAdjustments,
        draftAppendAdjustments: draftToPaperAdjustments,
        origEntries: dtdOrigEntries,
        staticPositionCodes,
        specialDtdPathCodes,
    }), [dtdPairingCtx, fromPaperUserEdited, toUserEditedCodeSet, pendingAppendCodeSet, pendingAppendAdjustments, draftToPaperAdjustments, dtdOrigEntries, staticPositionCodes, specialDtdPathCodes]);

    const {drItems, crItems} = useMemo(() => {
        if (!lineContexts) return {drItems: [], crItems: []};
        try {
            const classified = classifyLinesForDrCrView(lineContexts, extraRowKeys, classifyOptions);
            if (taxSalePositiveOnlyEdit && taxSaleLockedPositionsRef.current.size === 0) {
                for (const item of [...classified.drItems, ...classified.crItems]) {
                    taxSaleLockedPositionsRef.current.set(getTaxSaleRenderRowKey(item), item.position);
                }
            }
            if (!taxSalePositiveOnlyEdit || taxSaleLockedPositionsRef.current.size === 0) {
                return classified;
            }
            const locked = taxSaleLockedPositionsRef.current;
            const dr: DrCrRenderItem[] = [];
            const cr: DrCrRenderItem[] = [];
            for (const item of [...classified.drItems, ...classified.crItems]) {
                const position = locked.get(getTaxSaleRenderRowKey(item)) ?? item.position;
                if (position === 'Dr') {
                    dr.push(item);
                } else {
                    cr.push(item);
                }
            }
            return {drItems: dr, crItems: cr};
        } catch {
            return {drItems: [], crItems: []};
        }
    }, [lineContexts, extraRowKeys, classifyOptions, taxSalePositiveOnlyEdit]);

    // Compute which row keys are already shown (for the picker exclusion)
    const shownKeys = useMemo(() => {
        const keys = new Set<string>();
        for (const item of drItems) keys.add(`${item.code}:${item.arrayName}:${item.rowIndex}`);
        for (const item of crItems) keys.add(`${item.code}:${item.arrayName}:${item.rowIndex}`);
        return keys;
    }, [drItems, crItems]);

    const renderedItems = useMemo(() => [...drItems, ...crItems], [drItems, crItems]);
    const toCodeSet = useMemo(() => new Set(toCodes), [toCodes]);

    // Balance
    const drTotal = useMemo(() => drItems.reduce((sum, r) => sum + Math.abs(r.adjustment), 0), [drItems]);
    const crTotal = useMemo(() => crItems.reduce((sum, r) => sum + Math.abs(r.adjustment), 0), [crItems]);
    const isBalanced = Math.abs(drTotal - crTotal) <= 0.01 && (drTotal > 0 || crTotal > 0);

    const diffAmount = Math.abs(drTotal - crTotal);
    /** 合计较大的一侧（heavySide）；Append 时补平侧 = 其对面，见 computeAppendBalanceAdjustment */
    const diffSide: 'Dr' | 'Cr' = drTotal >= crTotal ? 'Dr' : 'Cr';

    useEffect(() => {
        onBalanceChange?.(isBalanced, diffAmount, diffSide);
    }, [isBalanced, diffAmount, diffSide, onBalanceChange]);

    // Endpoints list for the picker
    const pickerEndpoints = useMemo(() => {
        return (lineContexts ?? []).map((ctx) => ({
            code: ctx.code,
            codeName: ctx.codeName,
            entry: ctx.originalEntry,
            parDetail: ctx.parDetail,
        }));
    }, [lineContexts]);

    // Compute available (unshown) row counts per endpoint code for Add Row icon visibility
    const availableRowCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const ep of pickerEndpoints) {
            const allRows = scanAllRows(ep.parDetail);
            const available = allRows.filter(r => !shownKeys.has(`${ep.code}:${r.arrayName}:${r.rowIndex}`));
            counts[ep.code] = available.length;
        }
        return counts;
    }, [pickerEndpoints, shownKeys]);

    const handleAddRow = useCallback((code: string, arrayName: string, rowIndex: number) => {
        setExtraRowKeys(prev => {
            const next = new Set(prev);
            next.add(`${code}:${arrayName}:${rowIndex}`);
            return next;
        });
    }, []);

    const handleDeleteRow = useCallback((code: string, arrayName: string, rowIndex: number) => {
        const targetKey = `${code}:${arrayName}:${rowIndex}`;
        const hasOtherRenderedRows = renderedItems.some(
            item => item.code === code && `${item.code}:${item.arrayName}:${item.rowIndex}` !== targetKey
        );

        // Remove from extraRowKeys
        setExtraRowKeys(prev => {
            const next = new Set(prev);
            next.delete(targetKey);
            return next;
        });
        // Reset adjustment to 0 so the row won't appear in classified results
        handleAdjustmentChange(code, arrayName, rowIndex, 0);

        // If this toPaper endpoint no longer has any rendered rows, remove endpoint from current page.
        // FsSelectionDrawer will then include this code again because it is no longer in toPaper.codes.
        if (!hasOtherRenderedRows && toCodeSet.has(code)) {
            onEndpointDepleted?.(code);
        }
    }, [handleAdjustmentChange, renderedItems, toCodeSet, onEndpointDepleted]);

    return (
        <Box sx={{
            p: 2,
            // WHY: 随内容自然增高；父 Dialog 达 maxHeight 后 flex 收缩，表区才出现滚动条
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxSizing: 'border-box',
        }}>
            {/* Top bar: Balance summary (left) + Append Another Record button (right) — fixed */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
                flexShrink: 0,
            }}>
                <Box sx={{display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
                    <Typography variant="body2">
                        Dr Total: <strong>{formatAmount(drTotal)}</strong>
                    </Typography>
                    <Typography variant="body2">
                        Cr Total: <strong>{formatAmount(crTotal)}</strong>
                    </Typography>
                    {isBalanced && drTotal > 0 ? (
                        <Chip
                            icon={<CheckCircleOutlineIcon/>}
                            label="Balanced"
                            color="success"
                            size="small"
                            variant="outlined"
                        />
                    ) : (drTotal > 0 || crTotal > 0) ? (
                        <Chip
                            icon={<ErrorOutlineIcon/>}
                            label={`Diff: ${formatAmount(Math.abs(drTotal - crTotal))}`}
                            color="error"
                            size="small"
                            variant="outlined"
                        />
                    ) : null}
                </Box>
                {!readOnly && onAddToPaper && !isBalanced && (drTotal > 0 || crTotal > 0) && (
                    <Button size="small" variant="outlined" startIcon={<AddIcon/>} onClick={onAddToPaper}>
                        Append Another Record
                    </Button>
                )}
            </Box>
            {/* 未触达 Dialog 上限时无强制高度 → 无滚动条；触达后表体滚动，表头 sticky */}
            <TableContainer
                component={Paper}
                sx={{flex: '1 1 auto', minHeight: 0, overflow: 'auto'}}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        // Opaque sticky head so scrolling rows do not bleed through action.hover alpha
                        '& .MuiTableCell-stickyHeader': {
                            backgroundColor: (theme) => (
                                theme.palette.mode === 'dark'
                                    ? theme.palette.grey[800]
                                    : theme.palette.grey[100]
                            ),
                            zIndex: 3,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{py: 0.5, width: 200}}/>
                            <TableCell align="center" sx={{fontWeight: 600, width: 140, py: 0.5}}>Dr.</TableCell>
                            <TableCell align="center" sx={{fontWeight: 600, width: 140, py: 0.5}}>Cr.</TableCell>
                            {!readOnly && (
                                <TableCell align="center" sx={{fontWeight: 600, width: 80, py: 0.5}}>Action</TableCell>
                            )}
                        </TableRow>
                    </TableHead>

                    {/* Dr section */}
                    <DrCrSection
                        title="Dr."
                        color="primary.main"
                        items={drItems}
                        onAdjustmentChange={handleAdjustmentChange}
                        onItemNameChange={onItemNameChange}
                        onBankFieldChange={onBankFieldChange}
                        bankFieldErrors={bankFieldErrors}
                        onAddRow={(code) => setPickerForCode(code)}
                        onDeleteRow={handleDeleteRow}
                        readOnly={readOnly}
                        currencySymbol={currencySymbol}
                        availableRowCounts={availableRowCounts}
                        frozenFromKeys={frozenFromKeys}
                        taxSalePositiveOnlyEdit={taxSalePositiveOnlyEdit}
                        taxSaleLockedPositions={taxSaleLockedPositionsRef.current}
                        specialDtdPathCodes={specialDtdPathCodes}
                    />

                    {/* Cr section */}
                    <DrCrSection
                        title="Cr."
                        color="secondary.main"
                        items={crItems}
                        onAdjustmentChange={handleAdjustmentChange}
                        onItemNameChange={onItemNameChange}
                        onBankFieldChange={onBankFieldChange}
                        bankFieldErrors={bankFieldErrors}
                        onAddRow={(code) => setPickerForCode(code)}
                        onDeleteRow={handleDeleteRow}
                        readOnly={readOnly}
                        currencySymbol={currencySymbol}
                        availableRowCounts={availableRowCounts}
                        frozenFromKeys={frozenFromKeys}
                        taxSalePositiveOnlyEdit={taxSalePositiveOnlyEdit}
                        taxSaleLockedPositions={taxSaleLockedPositionsRef.current}
                        specialDtdPathCodes={specialDtdPathCodes}
                    />
                </Table>
            </TableContainer>

            {/* Empty state */}
            {drItems.length === 0 && crItems.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{py: 3, textAlign: 'center', flexShrink: 0}}>
                    No adjustments found. Enter adjustment values in the working papers first, or add rows below.
                </Typography>
            )}

            {/* Remark field — fixed below table scroll */}
            {onRemarkChange && (
                <Box sx={{mt: 2, flexShrink: 0}}>
                    <TextField
                        label="Remark"
                        multiline
                        rows={2}
                        fullWidth
                        value={remark || ''}
                        onChange={(e) => onRemarkChange(e.target.value)}
                        size="small"
                        variant="outlined"
                    />
                </Box>
            )}


            {/* Row picker dialog */}
            <RowPickerDialog
                open={pickerForCode !== null}
                onClose={() => setPickerForCode(null)}
                endpoints={pickerForCode ? pickerEndpoints.filter(ep => ep.code === pickerForCode) : pickerEndpoints}
                shownKeys={shownKeys}
                onSelect={handleAddRow}
            />

        </Box>
    );
}
